import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Coupon {
  id: string;
  provider_id: string;
  code: string;
  name: string;
  description?: string;
  discount_type: 'percentage' | 'fixed' | 'weekly_credits' | 'monthly_credits';
  discount_value: number;
  credits_per_period?: number;
  max_discount_amount?: number;
  min_order_amount?: number;
  usage_limit?: number;
  used_count: number;
  is_active: boolean;
  valid_from: string;
  valid_until?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

export interface CouponService {
  id: string;
  coupon_id: string;
  service_id: string;
  created_at: string;
}

export interface CreateCouponData {
  code: string;
  name: string;
  description?: string;
  discount_type: 'percentage' | 'fixed' | 'weekly_credits' | 'monthly_credits';
  discount_value?: number;
  credits_per_period?: number;
  max_discount_amount?: number;
  min_order_amount?: number;
  usage_limit?: number;
  valid_from?: string;
  valid_until?: string;
  service_ids: string[];
}

export const useCoupons = (providerId?: string) => {
  return useQuery({
    queryKey: ['coupons', providerId],
    queryFn: async () => {
      if (!providerId) return [];

      // Always fetch all coupons (including deleted) for better UX
      const { data, error } = await supabase
        .from('coupons')
        .select(`
          *,
          coupon_services (
            id,
            coupon_id,
            service_id,
            created_at,
            services (
              id,
              name
            )
          )
        `)
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching coupons:', error);
        throw error;
      }

      return data as (Coupon & {
        coupon_services: (CouponService & {
          services: { id: string; name: string };
        })[];
      })[];
    },
    enabled: !!providerId,
  });
};

export const useCreateCoupon = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ providerId, couponData }: { providerId: string; couponData: CreateCouponData }) => {
      console.log('Creating coupon with data:', { providerId, couponData });

      // Validate required fields
      if (!providerId) {
        throw new Error('Provider ID is required');
      }

      if (!couponData.code || !couponData.name) {
        throw new Error('Code and name are required');
      }

      if (!couponData.service_ids || couponData.service_ids.length === 0) {
        throw new Error('At least one service must be selected');
      }

      const isCreditBased = couponData.discount_type === 'weekly_credits' || couponData.discount_type === 'monthly_credits';

      if (!isCreditBased && (couponData.discount_value === undefined || couponData.discount_value <= 0)) {
        throw new Error('Discount value is required for non-credit based coupons');
      }

      if (isCreditBased && (!couponData.credits_per_period || couponData.credits_per_period <= 0)) {
        throw new Error('Credits per period is required for credit-based coupons');
      }

      // Create the coupon first - handle different coupon types properly
      const couponInsertData = {
        provider_id: providerId,
        code: couponData.code.toUpperCase(),
        name: couponData.name,
        description: couponData.description || null,
        discount_type: couponData.discount_type,
        discount_value: isCreditBased ? 0 : (couponData.discount_value || 0),
        credits_per_period: isCreditBased ? couponData.credits_per_period : null,
        max_discount_amount: !isCreditBased && couponData.max_discount_amount ? couponData.max_discount_amount : null,
        min_order_amount: !isCreditBased && couponData.min_order_amount ? couponData.min_order_amount : null,
        usage_limit: couponData.usage_limit || null,
        valid_from: couponData.valid_from || new Date().toISOString(),
        valid_until: couponData.valid_until && couponData.valid_until.trim() !== '' ? couponData.valid_until : null,
      };

      console.log('Inserting coupon with data:', couponInsertData);

      const { data: coupon, error: couponError } = await supabase
        .from('coupons')
        .insert(couponInsertData)
        .select()
        .single();

      if (couponError) {
        console.error('Coupon creation error:', couponError);
        throw new Error(`Failed to create coupon: ${couponError.message}`);
      }

      console.log('Coupon created successfully:', coupon);

      // Create coupon-service associations
      if (couponData.service_ids.length > 0) {
        const couponServices = couponData.service_ids.map(serviceId => ({
          coupon_id: coupon.id,
          service_id: serviceId,
        }));

        console.log('Creating coupon services:', couponServices);

        const { error: servicesError } = await supabase
          .from('coupon_services')
          .insert(couponServices);

        if (servicesError) {
          console.error('Coupon services error:', servicesError);
          throw new Error(`Failed to associate services: ${servicesError.message}`);
        }
      }

      return coupon;
    },
    onSuccess: (data) => {
      console.log('Coupon creation successful:', data);
      toast({
        title: "Coupon Created",
        description: "Your coupon has been created successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
    },
    onError: (error: any) => {
      console.error('Coupon creation failed:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create coupon. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateCoupon = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      couponId, 
      updates, 
      serviceIds 
    }: { 
      couponId: string; 
      updates: Partial<Coupon>; 
      serviceIds?: string[];
    }) => {
      // Update the coupon basic info
      const { data: couponData, error: couponError } = await supabase
        .from('coupons')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', couponId)
        .select()
        .single();

      if (couponError) {
        throw couponError;
      }

      // Update service associations if provided
      if (serviceIds !== undefined) {
        // First, delete existing associations
        const { error: deleteError } = await supabase
          .from('coupon_services')
          .delete()
          .eq('coupon_id', couponId);

        if (deleteError) {
          throw deleteError;
        }

        // Then, create new associations
        if (serviceIds.length > 0) {
          const couponServices = serviceIds.map(serviceId => ({
            coupon_id: couponId,
            service_id: serviceId,
          }));

          const { error: insertError } = await supabase
            .from('coupon_services')
            .insert(couponServices);

          if (insertError) {
            throw insertError;
          }
        }
      }

      return couponData;
    },
    onSuccess: () => {
      toast({
        title: "Coupon Updated",
        description: "Your coupon has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
    },
    onError: (error: any) => {
      console.error('Update coupon error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update coupon. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteCoupon = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (couponId: string) => {
      const { error } = await supabase
        .from('coupons')
        .update({ 
          deleted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', couponId);

      if (error) {
        console.error('Error deleting coupon:', error);
        throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: "Coupon Deleted",
        description: "Your coupon has been deleted successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
    },
    onError: (error: any) => {
      console.error('Delete coupon error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete coupon. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useRestoreCoupon = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (couponId: string) => {
      const { error } = await supabase
        .from('coupons')
        .update({ 
          deleted_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', couponId);

      if (error) {
        console.error('Error restoring coupon:', error);
        throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: "Coupon Restored",
        description: "Your coupon has been restored successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
    },
    onError: (error: any) => {
      console.error('Restore coupon error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to restore coupon. Please try again.",
        variant: "destructive",
      });
    },
  });
};
